import React, { useEffect, useMemo, useState } from "react";
import { SideNav } from "components";
import {
  LargeButton,
  MainBody,
  OverlayLoader,
  PageTitle,
} from "components/core";
import CustomTabs from "./CustomTabs";
import { useLocation, useNavigate } from "react-router";
import useLocalData from "hooks/useLocalData";
import { DisplayName } from "utlis/utils";
import http from "services/httpService";
import { WidthLimiter } from "pages/PlanSetup/styles";
import { HomeWrapper } from "components/CdBalance/style";
import routes from "components/routes";

const GradeSetup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [attributes, setAttributes] = useState([]);
  const [grades, setGrades] = useState([{}]);
  const [loading, setLoading] = useState(true);
  const [defaultIndex, setDefaultIndex] = useState(0);
  // Remove validation error state since we're handling it in GradeView
  // const [hasValidationErrors, setHasValidationErrors] = useState(false);

  let selectedValues = localStorage.getItem("eb_selected_values");
  if (selectedValues) {
    selectedValues = JSON.parse(selectedValues);
  }
  const userplanId = localStorage.getItem("eb_current_plan");
  const productName = useMemo(() => {
    if (!selectedValues || !userplanId) return;
    const name =
      selectedValues?.userplans_data[userplanId]?.product_type?.display_name ||
      selectedValues?.userplans_data[userplanId]?.product_type
        ?.product_type_name;
    return DisplayName(name);
  }, [userplanId, selectedValues]);

  const getGradeSelectables = async () => {
    const { data } = await http.get("/business/get_grade_setup_selectables/", {
      params: {
        userplan_id: userplanId,
      },
    });
    if (data) setAttributes(data);
  };

  const allowGradeAddition = useMemo(() => {
    if (!selectedValues) return false;
    return selectedValues.userplans_data[userplanId]?.flat_or_graded !== "FLAT";
  }, [selectedValues, userplanId]);

  const getGradeSelected = async () => {
    try {
      const { data } = await http.get("business/userplans_data_selected/");
      localStorage.setItem("eb_selected_values", JSON.stringify(data));
      const gradesData = data.userplans_data[userplanId].userplan_grades_data;
      if (gradesData.length > 0) {
        setGrades(gradesData);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const doesGradesExist = useMemo(() => {
    if (!selectedValues) return false;
    const currentPlan =
      selectedValues.userplans_data[userplanId]?.userplan_grades_data.length >
      0;
    if (currentPlan) return true;
    return false;
  }, [selectedValues, userplanId]);

  const isPostIssuance = useMemo(() => {
    if (location?.state?.postIssuance) return true;
    return false;
  }, [location]);

  useEffect(() => {
    (async function () {
      const { state } = location;
      if (state) {
        setDefaultIndex(state.mappingId);
      }
      if (userplanId) {
        setLoading(true);
        await getGradeSelected();
        await getGradeSelectables();
        setTimeout(() => {
          setLoading(false);
        }, 400);
      }
    })();
  }, [location, userplanId]);

  const handleGradeAddition = (value) => {
    setGrades((prev) => [...prev, value]);
  };

  const refreshGradesData = async () => {
    try {
      await getGradeSelected();
    } catch (error) {
      console.error("Error refreshing grades data:", error);
    }
  };
  const handleGradeDeletion = async (index) => {
    try {
      const gradeToDelete = grades[index];
      
      // If it's an existing grade with an ID, call the delete API
      if (gradeToDelete?.plan_grade_mapping_id) {
        const payload = {
          userplan_id: userplanId,
          plan_grade_mapping_id: gradeToDelete.plan_grade_mapping_id,
        };
        
        await http.post("/business/delete_grade/", payload);
      }
      
      // Remove the grade from the local state
      setGrades((prev) => prev.filter((_, i) => i !== index));
      
      // If we're deleting the currently selected grade, switch to the first grade
      if (index === 0 && grades.length > 1) {
        // Stay on the first grade (which will now be the second grade)
      } else if (index > 0) {
        // Stay on the same index, but it will now be the previous grade
      }
      
      // Refresh the grades data to ensure consistency
      await refreshGradesData();
    } catch (error) {
      console.error("Error deleting grade:", error);
    }
  };

  const isFlat = useMemo(() => {
    if (!selectedValues) return false;
    return selectedValues.userplans_data[userplanId]?.flat_or_graded === "FLAT";
  }, [selectedValues, userplanId]);

  // Remove validation change handler since we're handling it in GradeView
  // const handleValidationChange = (hasErrors) => {
  //   setHasValidationErrors(hasErrors);
  // };

  const redirectToGradeSummary = () => {
    navigate("/grade-setup/exclusion", {
      state: { setup: true }
    });
    // if (isPostIssuance) {
    //   navigate(`/${routes.CARE_DASHBOARD}`);
    //   return;
    // }
    // if (!isFlat) {
    //   navigate("./grade");
    //   return;
    // }

    // const productType =
    //   selectedValues.userplans_data[userplanId].product_type.product_type_name;

    // if (productType === "health_insurance") {  //GMC
    //   navigate(`/${routes.PRE_CORPORATE_BUFFER}`);
    // } else {
    //   navigate(`/${routes.UNIVERSAL_COVERAGE_DOC}`);
    // }
  };
  return loading ? (
    <OverlayLoader />
  ) : (
    <>
      <SideNav />

      <HomeWrapper style={{ overflowX: 'hidden' }}>
        <PageTitle title={`Setup ${productName}`} />
        <section
          style={{
            maxWidth: "930px",
            width: "100%",
            overflowX: "hidden",
            boxSizing: "border-box"
          }}
        >
          <div
            style={{
              marginTop: "36px",
              overflowX: "hidden"
            }}
          >
            <CustomTabs
              handleGradeDeletion={handleGradeDeletion}
              isPostIssuance={isPostIssuance}
              grades={grades}
              attributes={attributes}
              allowGradeAddition={allowGradeAddition}
              handleGradeAddition={handleGradeAddition}
              isFlat={isFlat}
              defaultIndex={defaultIndex}
              refreshGradesData={refreshGradesData}
            />
          </div>
          <div
            style={{
              marginTop: "5rem",
            }}
          >
            <LargeButton
              disabled={!doesGradesExist}
              label="Submit"
              style={{
                marginTop: "8rem",
                opacity: doesGradesExist ? "1" : "0.5",
              }}
              onClick={redirectToGradeSummary}
            />
          </div>
        </section>
      </HomeWrapper>
    </>
  );
};

export default GradeSetup;